import { useEffect, useRef, useState } from "react";
import { Wand2, Scissors, Save, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, streamPost } from "@/lib/api";
import { ContinueBox } from "./ContinueBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PlotForm = ({ ep, onStart, streaming }) => {
  const [p, setP] = useState(ep.plot || { premise: "", characters: "", tone: ep.tone || "", setting: ep.setting || "", minutes: 20 });
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  return (
    <div className="panel p-5 space-y-3" data-testid="plot-form">
      <div className="flex items-center gap-2 text-amber-400 font-mono text-xs tracking-widest"><Sparkles className="w-3.5 h-3.5" /> WRITE FROM A PLOT</div>
      {ep.previous_title && <p className="text-[11px] text-cyan-400" data-testid="plot-follows-note">Picks up from the ending of "{ep.previous_title}" with the returning cast.</p>}
      <Textarea data-testid="plot-premise-input" rows={4} placeholder="Premise / story plot — what happens in this episode?" value={p.premise} onChange={set("premise")} />
      <Textarea data-testid="plot-characters-input" rows={2} placeholder="Main characters (names, who they are)" value={p.characters} onChange={set("characters")} />
      <Input data-testid="plot-tone-input" placeholder="Tone (e.g. light comedy, tense thriller)" value={p.tone} onChange={set("tone")} />
      <Input data-testid="plot-setting-input" placeholder="Setting" value={p.setting} onChange={set("setting")} />
      <div className="flex items-center gap-3">
        <Input data-testid="plot-minutes-input" type="number" min={1} max={30} className="w-24" value={p.minutes} onChange={(e) => setP({ ...p, minutes: e.target.value === "" ? "" : Number(e.target.value) })} />
        <span className="text-xs text-slate-400">minutes</span>
      </div>
      <Button data-testid="write-script-btn" disabled={streaming || !p.premise.trim()} onClick={() => onStart({ ...p, minutes: Number(p.minutes) || 20 })} className="w-full rounded-full">
        {streaming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
        {streaming ? "Writing script…" : "Write the script with AI"}
      </Button>
    </div>
  );
};

const RewriteBox = ({ selection, onRewrite, busy }) => {
  const [ins, setIns] = useState("");
  return (
    <div className="panel p-5 space-y-3" data-testid="rewrite-box">
      <div className="font-mono text-xs tracking-widest text-cyan-400">REWRITE A PART</div>
      <p className="text-xs text-slate-400">
        {selection ? `Selected ${selection.length} characters.` : "Select text in the script, then tell AI how to change it."}
      </p>
      <Input data-testid="rewrite-instruction-input" placeholder='e.g. "make this funnier", "shorten this scene"' value={ins} onChange={(e) => setIns(e.target.value)} />
      <Button data-testid="rewrite-btn" variant="secondary" disabled={!selection || !ins.trim() || busy} onClick={() => onRewrite(ins)} className="w-full rounded-full">
        {busy ? "Rewriting…" : "Rewrite selection"}
      </Button>
    </div>
  );
};

export const ScriptTab = ({ ep, scenes, reload, setTab }) => {
  const [script, setScript] = useState(ep.script || "");
  const [streaming, setStreaming] = useState(false);
  const [sel, setSel] = useState({ start: 0, end: 0 });
  const ta = useRef(null);
  const dirty = script !== (ep.script || "");
  useEffect(() => { if (!streaming) setScript(ep.script || ""); }, [ep.script]); // eslint-disable-line

  const save = async () => {
    await http.patch(`/episodes/${ep.id}`, { script });
    await reload();
  };

  const write = async (plot) => {
    if (script.trim() && !window.confirm("Replace the current script with a new AI-written one?")) return;
    setStreaming(true); setScript("");
    try {
      await streamPost(`/episodes/${ep.id}/write-script`, plot, (t) => setScript((s) => s + t));
      toast.success("Script written. Edit it freely, then split into scenes.");
      await reload();
    } catch (e) { toast.error(e.message); } finally { setStreaming(false); }
  };

  const rewrite = async (instruction) => {
    const { start, end } = sel;
    const raw = script.slice(start, end);
    const lead = raw.match(/^\s*/)[0], trail = raw.match(/\s*$/)[0];
    const selection = raw.trim();
    const before = script.slice(0, start) + lead, after = trail + script.slice(end);
    let out = "";
    setStreaming(true);
    try {
      await streamPost(`/episodes/${ep.id}/rewrite`, { selection, instruction, context: before.slice(-1500) + after.slice(0, 1500) },
        (t) => { out += t; setScript(before + out.trim() + after); });
      out = out.trim();
      await http.patch(`/episodes/${ep.id}`, { script: before + out + after });
      setSel({ start: before.length, end: before.length + out.length });
      toast.success("Part rewritten");
      await reload();
    } catch (e) { toast.error(e.message); } finally { setStreaming(false); }
  };

  const split = async () => {
    if (scenes.length && !window.confirm("Re-splitting replaces the current scene list. Continue?")) return;
    try {
      if (dirty) await http.patch(`/episodes/${ep.id}`, { script });
      await http.post(`/episodes/${ep.id}/split`);
      toast("Splitting into 20-second scenes… this can take a minute or two.");
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const continueStory = async (body) => {
    setStreaming(true);
    try {
      if (dirty) await http.patch(`/episodes/${ep.id}`, { script });
      const base = script.trimEnd();
      let out = "";
      await streamPost(`/episodes/${ep.id}/continue-script`, body, (t) => {
        out += t;
        setScript(base + out);
        if (ta.current) ta.current.scrollTop = ta.current.scrollHeight;
      });
      toast.success(scenes.length ? "Next part written. Review it, then add it as new scenes." : "Next part written.");
      await reload();
    } catch (e) { toast.error(e.message); } finally { setStreaming(false); }
  };

  const splitNew = async () => {
    try {
      if (dirty) await http.patch(`/episodes/${ep.id}`, { script });
      await http.post(`/episodes/${ep.id}/split-append`);
      toast("Adding the new part as scenes after the existing ones…");
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const splitting = ep.split_status === "running";
  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="script-tab">
      <div className="panel p-4 flex flex-col min-h-[70vh]">
        <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-[#2A3245]">
          <span className="font-mono text-xs tracking-widest text-slate-400">SCRIPT</span>
          <span className="font-mono text-xs text-slate-600" data-testid="script-word-count">{script.split(/\s+/).filter(Boolean).length} words</span>
          <div className="ml-auto flex gap-2">
            <Button data-testid="save-script-btn" size="sm" variant="secondary" disabled={!dirty || streaming} onClick={save} className="rounded-full">
              <Save className="w-3.5 h-3.5 mr-1.5" />Save
            </Button>
            <Button data-testid="split-script-btn" size="sm" disabled={!script.trim() || streaming || splitting} onClick={split} className="rounded-full">
              {splitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5 mr-1.5" />}
              {splitting ? "Splitting…" : scenes.length ? "Re-split all" : "Split into 20s scenes"}
            </Button>
          </div>
        </div>
        {ep.split_status === "failed" && <div className="text-xs text-red-400 pt-2" data-testid="split-error">Split failed: {ep.split_error}</div>}
        {ep.split_status === "done" && scenes.length > 0 && (
          <button data-testid="go-to-scenes" onClick={() => setTab("scenes")} className="text-xs text-cyan-400 pt-2 text-left hover:underline">
            {ep.last_split_added && scenes.length > ep.last_split_added ? `${ep.last_split_added} new scenes added (now ${scenes.length}) — review them →` : `${scenes.length} scenes ready — review them →`}
          </button>
        )}
        <textarea
          ref={ta} data-testid="script-textarea" value={script} readOnly={streaming}
          onChange={(e) => setScript(e.target.value)}
          onSelect={(e) => setSel({ start: e.target.selectionStart, end: e.target.selectionEnd })}
          placeholder={"Paste your full episode script here…\n\nINT. CAFÉ - MORNING\nMaya wipes the counter.\n\nMAYA\nGood morning! You're my first customer."}
          className="flex-1 mt-3 w-full bg-transparent resize-none outline-none font-mono text-[13px] leading-relaxed text-slate-200 placeholder:text-slate-600 min-h-[60vh]"
        />
      </div>
      <div className="space-y-5">
        {(ep.script || "").trim() && (
          <ContinueBox ep={ep} scenes={scenes} onContinue={continueStory} onSplitNew={splitNew} streaming={streaming} splitting={splitting} unsplitWords={ep.unsplit_words || 0} dirty={dirty} />
        )}
        <PlotForm ep={ep} onStart={write} streaming={streaming} />
        <RewriteBox selection={script.slice(sel.start, sel.end)} onRewrite={rewrite} busy={streaming} />
      </div>
    </div>
  );
};
